import React from 'react'
// @ts-ignore
import { RouteContext, useRouteContext } from 'plugin-liverig/shared/asset/hooks/RouteContext'
import Breadcrumb from './components/Breadcrumb'
import Utils from 'live/lib/utils'
import styled, { css } from 'styled-components'
import SideBar from './components/SideBar'
import {
    selectActiveOnly,
    selectFilter,
    selectNavigationLoading,
    selectOpen,
    selectSelectedDashboard,
    selectSelectedDashboardId,
    selectSelectedDashboardNode,
    selectSelectedTreeNode,
    setDashboardMode,
    setLoading,
    setSelectedDashboard,
    setSelectedTreeNode
} from './ducks/navigationSlice'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import DashboardsList from './components/DashboardsList'
import { DashboardsPage } from './components/DashboardsPage'
import DashboardRenderer from './components/DashboardRenderer'
import useSetParamsToRouter from 'live/lib/hooks/useSetParamsToRouter'
import { getDashboardFilters, getModifiedSpan } from '../utils'
import { LoadingComponent } from './components/LoadingComponent'

interface Props {
    data: DashboardNode[]
    loading: boolean
}

const Navigation: React.FC<Props> = ({ data, loading }) => {
    const homeId = data[0]?.id
    const homeNode = data[0]
    const sortOrder =
        data[0]?.sortOrder && data[0]?.sortOrder?.length > 0
            ? data[0].sortOrder
            : ['Time Dashboards', 'Depth Dashboards']
    const dispatch = useAppDispatch()
    const routeContext = useRouteContext()
    const setParamsToRouter = useSetParamsToRouter(true)

    const filters = useAppSelector(state => selectFilter(state))
    const activeOnly = useAppSelector(state => selectActiveOnly(state))
    const navigationLoading = useAppSelector(state => selectNavigationLoading(state))

    let selectedDashboard = useAppSelector(state => selectSelectedDashboard(state))
    let selectedDashboardNode = useAppSelector(state => selectSelectedDashboardNode(state))

    // Open Home Dashboard when app opens in Home Page or no node is selected
    if (!selectedDashboardNode || selectedDashboardNode.id == homeId) {
        if (!homeNode.dashboards) {
            console.warn(
                'No dashboards are configured for the Home Page. Please contact the system administrator to register one (only one) dashboard as Home Page.'
            )
        } else {
            if (homeNode.dashboards.length > 1) {
                console.warn('More than one dashboard is configured for the Home Page. Only the first one is used.')
            }
            selectedDashboard = homeNode.dashboards[0]
            selectedDashboardNode = homeNode
        }
    }

    const selectedTreeNode = useAppSelector(state => selectSelectedTreeNode(state))
    const selectedDashboardId = useAppSelector(state => selectSelectedDashboardId(state))
    const open = useAppSelector(state => selectOpen(state))
    const navigationContainerRef = React.useRef<HTMLDivElement>(null)

    const [dashboardListHeight, setDashboardListHeight] = React.useState(0)

    const nodeToDashboard = React.useMemo(() => {
        const aux: Array<SimplifiedNavigation> = []

        const convert = (node: DashboardNode, prevPath: DashboardNode[] = [], uuid: string) => {
            const path = [...(prevPath ? prevPath : []), node]

            uuid = uuid ? uuid + `|${node.uid}` : `${node.uid}`
            aux.push({
                id: node.uid,
                uid: uuid,
                dashboardId: node.dashboards?.[0]?.dashboardId,
                path: path,
                pageName: node.pageName,
                node,
                active: node.active ?? node.isActive,
                lookupKey: node.lookupKey
            })

            if (Array.isArray(node.childNodes)) node.childNodes?.map(node => convert(node, path, uuid))
        }

        data?.forEach((node: DashboardNode) => {
            convert(node, null, '')
        })

        return aux
    }, [data])

    React.useEffect(() => {
        dispatch(setLoading(loading))
    }, [loading])

    const getDashboardFromUrl = React.useCallback(
        event => {
            const paramsNew = new URLSearchParams(event.newURL.slice(event.newURL.indexOf('?') + 1))
            const paramsOld = new URLSearchParams(event.oldURL.slice(event.oldURL.indexOf('?') + 1))

            const dashIdNew = paramsNew.get('tree_selecteddashboard')
            const dashIdOld = paramsOld.get('tree_selecteddashboard')
            if (dashIdNew && dashIdOld && dashIdOld != dashIdNew)
                dispatch(
                    setSelectedDashboard(
                        selectedDashboardNode.dashboards.find(x => x.dashboardId.toString() == dashIdNew)
                    )
                )

            const mode = paramsNew.get('mode')
            dispatch(setDashboardMode(mode))
        },
        [selectedDashboardNode]
    )

    React.useEffect(() => {
        window.addEventListener('hashchange', getDashboardFromUrl, false)
        return () => {
            window.removeEventListener('hashchange', getDashboardFromUrl)
        }
    }, [getDashboardFromUrl])

    const getDropdownFilters = (nodes: SimplifiedNavigation[]) => {
        return nodes.reduce(
            (result: { [k: string]: { value: string; label: string }[] }, node: SimplifiedNavigation) => {
                if (node.node.nodeType && node.lookupKey) {
                    if (!result[node.node.nodeType]) result[node.node.nodeType] = []
                    if (!result[node.node.nodeType].find(x => x.label == node.lookupKey)) {
                        result[node.node.nodeType].push({ value: node.lookupKey, label: node.lookupKey })
                    }
                }

                return result
            },
            {}
        )
    }

    const dropdownFilters = React.useMemo(() => {
        return getDropdownFilters(nodeToDashboard)
    }, [nodeToDashboard])

    const renderSideBar = React.useMemo(() => {
        //prevent from reloading Sidebar every time a new dashboard is selected
        return <SideBar baseNodes={data} nodeToDashboard={nodeToDashboard} dropdownFilters={dropdownFilters} />
    }, [data, dropdownFilters])

    React.useEffect(() => {
        localStorage.setItem(`user/${window.Live.currentUser.id}/config/dashboard/tips`, '{"widget-view":false}')
    }, [])

    React.useEffect(() => {
        if (!selectedTreeNode) {
            dispatch(setSelectedTreeNode(data[0]?.id))
            return
        }

        if (!selectedDashboard || (selectedDashboard && selectedDashboardNode.showPage)) {
            setParamsToRouter({
                tree_filters: convertFilterToQueryString(filters, activeOnly),
                tree_selectednode: selectedTreeNode,
                ...(selectedDashboard ? { tree_selecteddashboard: selectedDashboard.dashboardId } : {})
            })
        }
    }, [filters, activeOnly, selectedTreeNode, selectedDashboardNode, selectedDashboard])

    React.useEffect(() => {
        if (!selectedDashboard && selectedDashboardId && selectedDashboardNode?.showPage) {
            dispatch(
                setSelectedDashboard(selectedDashboardNode.dashboards.find(x => x.dashboardId === selectedDashboardId))
            )
        }
    }, [selectedDashboard, selectedDashboardNode, selectedDashboardId])

    const convertFilterToQueryString = (filters, activeOnly) => {
        if (!filters) return
        return Object.entries({ ...filters, active: [activeOnly] })
            .map(([key, value]) => {
                return value && value.length > 0
                    ? `tree_${key}=${encodeURIComponent(
                          Object.entries(value)
                              .map(([k, v]) => v)
                              .join(',')
                      )}`
                    : null
            })
            .filter(x => x)
            .join('&')
    }

    const dashboardFilter = React.useMemo(() => {
        try {
            // if (!selectedDashboard || !selectedDashboard?.filter || selectedDashboard?.filter.trim() == "") return {}
            // const obj = JSON.parse('{"' + selectedDashboard.filter.replace("?", "").replace(/&/g, '","').replace(/=/g,'":"').replace('"{', "{").replace('}"', "}").replace("\u0026", ",") + '"}')
            // return Object.keys(obj).reduce((pV, cV) => {
            //   return { ...pV, [cV]: typeof obj[cV] == "object" ? JSON.stringify(obj[cV]) : obj[cV] }
            // }, {})

            let obj = getDashboardFilters(selectedDashboard)

            if ((obj && (obj as any).span) || (selectedDashboard && selectedDashboard?.spanExpression)) {
                obj = {
                    ...obj,
                    ...getModifiedSpan(
                        selectedDashboard,
                        (obj as any).span ? (obj as any).span : selectedDashboard?.spanExpression,
                        selectedDashboardNode.active
                    )
                }
                return obj
            }
        } catch (err) {
            console.log(err)
            return {}
        }
    }, [selectedDashboard])

    return (
        <NavigationContainer
            ref={navigationContainerRef}
            isMobile={Utils.isMobile()}
            dashbaordListHeight={
                selectedDashboardNode?.dashboards && selectedDashboardNode?.dashboards.length > 1
                    ? dashboardListHeight
                    : 0
            }
        >
            {navigationLoading && <LoadingComponent width={navigationContainerRef.current.clientWidth} />}
            {renderSideBar}
            {!selectedDashboard ? (
                <DashboardsPage
                    homeId={homeId}
                    sortOrder={sortOrder}
                    selectedDashboardNode={selectedDashboardNode ?? data[0]}
                />
            ) : (
                <div className="dashboardContainer">
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: open ? '0px' : '32px' }}>
                        <Breadcrumb homeId={homeId} />
                        {selectedDashboardNode?.dashboards && selectedDashboardNode?.dashboards.length > 1 && (
                            <DashboardsList
                                getHeight={setDashboardListHeight}
                                selectedDashboard={selectedDashboard}
                                dashboards={
                                    selectedDashboardNode?.dashboards.filter(x => selectedDashboard.type == x.type) ??
                                    data[0].dashboards
                                }
                                fromPage={(selectedDashboardNode ?? data[0]).showPage}
                                dashboardsType={selectedDashboard.type}
                            />
                        )}
                    </div>
                    <RouteContext.Provider
                        value={{
                            entityId: selectedDashboard?.dashboardId ?? data[0].dashboards[0]?.dashboardId,
                            tab: 'dashboards',
                            query: {
                                ...routeContext.query,
                                ...dashboardFilter,
                                tree_filters: convertFilterToQueryString(filters, activeOnly),
                                tree_selectednode: selectedTreeNode,
                                ...(selectedDashboard ? { tree_selecteddashboard: selectedDashboard.dashboardId } : {})
                            },
                            assetURL: 'navigation'
                        }}
                    >
                        <DashboardRenderer />
                    </RouteContext.Provider>
                </div>
            )}
        </NavigationContainer>
    )
}

export default Navigation

const NavigationContainer = styled.div<{ dashbaordListHeight: number; isMobile }>`
    width: 100%;
    position: absolute;
    top: ${props => (props.isMobile ? 'unset' : '35px')};
    display: flex;
    /* padding-left: 32px; */

    height: ${props => (props.isMobile ? 'calc(100% - 50px)' : 'calc(100% - 35px)')};
    background: var(--gray-0);
    overflow: hidden;

    .dashboardContainer {
        height: ${props => (props.isMobile ? '100%' : `calc(100% - ${props.dashbaordListHeight + 37}px)`)};
        width: 100%;
    }
`
